import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../../utils/amplifyClient';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { UpdateEquipmentCategoryInput } from '../../API';
import { SingleUnit } from './equipmentTypes';
import { updateEquipmentCategory as updateEquipmentCategoryMutation } from '../../graphql/mutations';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useFormsContext } from '../../contexts/FormsContext';
import { useUserContext } from '../../contexts/UserContext';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import { resizeFile } from '../../utils/helperFunctions';
import { S3_IMAGE_PATH } from '../../utils/constants';
import { Btn, PageHeader } from '../ui';
import '../../css/SingleUnitContent.css';
import { EquipmentOverviewTab } from './EquipmentOverviewTab';
import { EquipmentUnsavedExitModal } from './EquipmentFormShared';
import { UnsavedChangesBar } from './UnsavedChangesBar';
import {
    NEW_UNIT_PRIORITY_FIELDS,
    ADD_EQUIPMENT_TYPE_OPTION_LABEL,
    ADD_INSPECTION_FORM_OPTION_LABEL,
    ADD_DAILY_SERVICE_FORM_OPTION_LABEL,
    PENDING_NEW_CATEGORY_ID,
    getEquipmentCategoryEditPath,
} from './equipmentConstants';
import { normalizeUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { ensureCompanyUnitsOfMeasureSeeded } from '../../utils/companyUnitOfMeasureCatalog';
import { normalizeStoredYearValue } from '../../utils/dateInputUtils';
import { saveNewEquipmentCategory } from './equipmentCategorySave';
import { EquipmentCategoryFormOverlay } from './EquipmentCategoryFormOverlay';
import { createSingleUnitMutation } from './equipmentMutations';
import { sanitizeGen2SingleUnitWritePayload } from './equipmentFieldUtils';
import { useEquipmentUnitFieldConfigs } from './useEquipmentUnitFieldConfigs';
import { useEquipmentCategoryAutosuggest } from './useEquipmentCategoryAutosuggest';
import {
    getUnitNameValidationMessage,
    validateEquipmentCategorySelection,
    validateLicenseNumber,
    validateSerialNumber,
    validateEngineSerialNumber,
    validateUnitName,
    UnitNameValidationError,
} from './equipmentUnitNameUtils';
import { FieldFocusGuardConfig, FieldValidationConfig } from './equipmentTypes';
import { Card } from '../ui';
import { IconEquipment } from '@icons';
import { FormBuilderOverlay } from '../pages-forms';
import { hasPermission, Permission } from '../../utils/permissions';
import { FORM_TYPES } from '../../utils/constants';
import {
    clearEquipmentNewUnitDraft,
    clearSharedEquipmentNewUnitDrafts,
    equipmentNewUnitDraftKey,
    readEquipmentNewUnitDraft,
    writeEquipmentNewUnitDraft,
} from './equipmentNewUnitDraftStorage';

export const EquipmentNewUnitContent = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { companyGroupedEquipmentData, companyUnits, addUnit, addCategory, refreshEquipment, updateCategory } =
        useEquipmentContext();
    const { companyForms } = useFormsContext();
    const { company, companyOwner, companyUser, businessAdmin } = useUserContext();
    const { registerGuard, tryNavigate, confirmPendingNavigation, cancelPendingNavigation } = useNavigationGuard();

    const [formData, setFormData] = useState<Partial<SingleUnit>>({});
    const [newUnitInspectionFormId, setNewUnitInspectionFormId] = useState('');
    const [isSavingNewUnit, setIsSavingNewUnit] = useState(false);
    const [newUnitSaveError, setNewUnitSaveError] = useState<string | null>(null);
    const [unitNameError, setUnitNameError] = useState<UnitNameValidationError | null>(null);
    const [categoryIdError, setCategoryIdError] = useState<UnitNameValidationError | null>(null);
    const [serialNumberError, setSerialNumberError] = useState<UnitNameValidationError | null>(null);
    const [engineSerialNumberError, setEngineSerialNumberError] = useState<UnitNameValidationError | null>(null);
    const [licenseNumberError, setLicenseNumberError] = useState<UnitNameValidationError | null>(null);
    const [showNewUnitExitModal, setShowNewUnitExitModal] = useState(false);
    const [newUnitImage, setNewUnitImage] = useState<File | null>(null);
    const [newUnitImagePreviewUrl, setNewUnitImagePreviewUrl] = useState('');
    const [pendingCategoryName, setPendingCategoryName] = useState<string | null>(null);
    const [showCategoryOverlay, setShowCategoryOverlay] = useState(false);
    const [showInspectionFormOverlay, setShowInspectionFormOverlay] = useState(false);
    const [pendingInspectionFormName, setPendingInspectionFormName] = useState('');
    const [showDailyServiceFormOverlay, setShowDailyServiceFormOverlay] = useState(false);
    const [pendingDailyServiceFormName, setPendingDailyServiceFormName] = useState('');

    const canManageForms = hasPermission(companyOwner, companyUser, Permission.MANAGE_FORMS, businessAdmin);
    const companyId = (company?.id as string) || companyOwner?.companyId || '';

    const formsList = useMemo(() => [...companyForms].sort((a, b) => a.name.localeCompare(b.name)), [companyForms]);
    const { sectionConfigs, newUnitFieldConfigs, reloadServiceCenters } =
        useEquipmentUnitFieldConfigs(formsList);

    const categoryId = formData.categoryId as string | undefined;
    const categoryAutosuggest = useEquipmentCategoryAutosuggest({ categoryId, formData });
    const { unitOfMeasureExtraValues, ...autosuggestOptions } = categoryAutosuggest;

    const hasNewUnitUnsavedChanges = useMemo(() => {
        return (
            !!categoryId ||
            !!pendingCategoryName ||
            !!formData.name ||
            !!newUnitInspectionFormId ||
            !!newUnitImage ||
            Object.values(formData).some((value) => value !== '' && value !== undefined && value !== null)
        );
    }, [formData, newUnitInspectionFormId, newUnitImage, categoryId, pendingCategoryName]);

    const draftUserId = (companyUser?.id as string) || (companyOwner?.id as string) || '';
    const draftStorageKey = useMemo(
        () => (draftUserId ? equipmentNewUnitDraftKey(companyId, draftUserId) : ''),
        [companyId, draftUserId],
    );
    const restoredDraftRef = useRef(false);
    const skipCategoryFormHydrateRef = useRef(false);
    const draftStorageKeyRef = useRef(draftStorageKey);
    draftStorageKeyRef.current = draftStorageKey;

    const loggedInUserName = companyOwner?.name || companyUser?.name || '-';

    const draftNewUnit = useMemo(() => {
        const today = new Date().toISOString().split('T')[0];
        const existingCategory = companyGroupedEquipmentData.find((c) => c.id === categoryId);
        const category =
            categoryId === PENDING_NEW_CATEGORY_ID && pendingCategoryName
                ? { name: pendingCategoryName, formId: newUnitInspectionFormId || null }
                : existingCategory
                  ? { name: existingCategory.name, formId: newUnitInspectionFormId || existingCategory.formId }
                  : undefined;
        return {
            id: 'draft-new-unit',
            equipmentStatus: true,
            categoryId,
            category,
            ...formData,
            name: (formData.name as string) || '',
            description: (formData.description as string) || '',
            inputDate: today,
            inputByPerson: loggedInUserName,
        } as SingleUnit;
    }, [
        formData,
        companyGroupedEquipmentData,
        newUnitInspectionFormId,
        categoryId,
        pendingCategoryName,
        loggedInUserName,
    ]);

    useEffect(() => {
        if (!newUnitImage) {
            setNewUnitImagePreviewUrl('');
            return;
        }
        const url = URL.createObjectURL(newUnitImage);
        setNewUnitImagePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [newUnitImage]);

    useEffect(() => {
        registerGuard(hasNewUnitUnsavedChanges ? { when: true, onBlocked: () => setShowNewUnitExitModal(true) } : null);
        return () => registerGuard(null);
    }, [hasNewUnitUnsavedChanges, registerGuard]);

    // Restore in-progress draft after remount (e.g. Mac lock/unlock auth refresh).
    useEffect(() => {
        if (restoredDraftRef.current) return;
        if (!draftUserId) return;
        clearSharedEquipmentNewUnitDrafts(companyId);
        if (!draftStorageKey) return;
        const draft = readEquipmentNewUnitDraft(draftStorageKey);
        if (!draft) return;
        restoredDraftRef.current = true;
        skipCategoryFormHydrateRef.current = true;
        setFormData(draft.formData as Partial<SingleUnit>);
        setNewUnitInspectionFormId(draft.newUnitInspectionFormId || '');
        setPendingCategoryName(draft.pendingCategoryName);
    }, [draftStorageKey, companyId, draftUserId]);

    // URL ?categoryId= preselect — only when no draft was restored.
    useEffect(() => {
        if (restoredDraftRef.current) return;
        const preselectedCategoryId = searchParams.get('categoryId');
        if (preselectedCategoryId) {
            setFormData((prev: Partial<SingleUnit>) => ({ ...prev, categoryId: preselectedCategoryId }));
        }
    }, [searchParams]);

    useEffect(() => {
        if (!categoryId || categoryId === PENDING_NEW_CATEGORY_ID) return;
        if (skipCategoryFormHydrateRef.current) {
            skipCategoryFormHydrateRef.current = false;
            return;
        }
        const category = companyGroupedEquipmentData.find((c) => c.id === categoryId);
        if (category?.formId) setNewUnitInspectionFormId(category.formId);
    }, [categoryId, companyGroupedEquipmentData]);

    // Persist dirty drafts so wake/remount does not lose work.
    useEffect(() => {
        if (!hasNewUnitUnsavedChanges || !draftStorageKey) return;
        const timer = window.setTimeout(() => {
            writeEquipmentNewUnitDraft(draftStorageKeyRef.current, {
                formData: formData as Record<string, unknown>,
                newUnitInspectionFormId,
                pendingCategoryName,
                queryCategoryId: searchParams.get('categoryId'),
            });
        }, 400);
        return () => window.clearTimeout(timer);
    }, [hasNewUnitUnsavedChanges, formData, newUnitInspectionFormId, pendingCategoryName, searchParams, draftStorageKey]);

    useEffect(() => {
        if (searchParams.get('editId')) {
            navigate(getEquipmentCategoryEditPath(searchParams.get('editId')!), { replace: true });
        }
    }, [searchParams, navigate]);

    const validateCategorySelectionForCreate = useCallback(
        (value: string) =>
            validateEquipmentCategorySelection(value, pendingCategoryName, companyGroupedEquipmentData),
        [pendingCategoryName, companyGroupedEquipmentData],
    );

    const validateUnitNameForCreate = useCallback(
        (value: string) => validateUnitName(value, companyUnits, companyGroupedEquipmentData),
        [companyUnits, companyGroupedEquipmentData],
    );

    const validateSerialNumberForCreate = useCallback(
        (value: string) => validateSerialNumber(value, companyUnits, companyGroupedEquipmentData),
        [companyUnits, companyGroupedEquipmentData],
    );

    const validateLicenseNumberForCreate = useCallback(
        (value: string) => validateLicenseNumber(value, companyUnits, companyGroupedEquipmentData),
        [companyUnits, companyGroupedEquipmentData],
    );

    const validateEngineSerialNumberForCreate = useCallback(
        (value: string) => validateEngineSerialNumber(value, companyUnits, companyGroupedEquipmentData),
        [companyUnits, companyGroupedEquipmentData],
    );

    const categoryIdFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: categoryIdError,
            validate: validateCategorySelectionForCreate,
            blockFocusUntilValid: true,
            onValidationError: setCategoryIdError,
        }),
        [categoryIdError, validateCategorySelectionForCreate],
    );

    const unitNameFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: unitNameError,
            validate: validateUnitNameForCreate,
            blockFocusUntilValid: true,
            onValidationError: setUnitNameError,
        }),
        [unitNameError, validateUnitNameForCreate],
    );

    const serialNumberFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: serialNumberError,
            validate: validateSerialNumberForCreate,
            blockFocusUntilValid: true,
            onValidationError: setSerialNumberError,
        }),
        [serialNumberError, validateSerialNumberForCreate],
    );

    const licenseNumberFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: licenseNumberError,
            validate: validateLicenseNumberForCreate,
            blockFocusUntilValid: true,
            onValidationError: setLicenseNumberError,
        }),
        [licenseNumberError, validateLicenseNumberForCreate],
    );

    const engineSerialNumberFieldValidation = useMemo<FieldValidationConfig>(
        () => ({
            error: engineSerialNumberError,
            validate: validateEngineSerialNumberForCreate,
            blockFocusUntilValid: true,
            onValidationError: setEngineSerialNumberError,
        }),
        [engineSerialNumberError, validateEngineSerialNumberForCreate],
    );

    const fieldValidations = useMemo(
        () => ({
            categoryId: categoryIdFieldValidation,
            name: unitNameFieldValidation,
            serialNumber: serialNumberFieldValidation,
            licenseNumber: licenseNumberFieldValidation,
            engineSerialNumber: engineSerialNumberFieldValidation,
        }),
        [
            categoryIdFieldValidation,
            unitNameFieldValidation,
            serialNumberFieldValidation,
            licenseNumberFieldValidation,
            engineSerialNumberFieldValidation,
        ],
    );

    const unitNameFocusGuard = useMemo<FieldFocusGuardConfig>(
        () => ({
            validate: () => validateCategorySelectionForCreate(categoryId || ''),
            onBlocked: setCategoryIdError,
            refocusElementId: 'new-unit-categoryId',
        }),
        [categoryId, validateCategorySelectionForCreate],
    );

    const handlePendingCategoryAdd = useCallback(({ searchText }: { searchText: string }) => {
        setCategoryIdError(null);
        setPendingCategoryName(searchText.trim() || null);
        setShowCategoryOverlay(true);
    }, []);

    const handleCategoryIdChange = (value: string) => {
        setCategoryIdError(null);
        if (value === PENDING_NEW_CATEGORY_ID) return;
        setPendingCategoryName(null);
        setFormData((prev: Partial<SingleUnit>) => {
            const prevId = (prev as Record<string, string | undefined>).categoryId;
            const switchedCategory =
                Boolean(prevId) && prevId !== value && prevId !== PENDING_NEW_CATEGORY_ID;
            if (!switchedCategory) return { ...prev, categoryId: value };
            return { ...prev, categoryId: value, manufacturerName: '', modelNumber: '' };
        });
    };

    const handlePendingInspectionFormAdd = useCallback(({ searchText }: { searchText: string }) => {
        setPendingInspectionFormName(searchText.trim());
        setShowInspectionFormOverlay(true);
    }, []);

    const handlePendingDailyServiceFormAdd = useCallback(({ searchText }: { searchText: string }) => {
        setPendingDailyServiceFormName(searchText.trim());
        setShowDailyServiceFormOverlay(true);
    }, []);

    const handleSaveNewUnit = async () => {
        const selectedCategoryId = formData.categoryId as string | undefined;
        const unitName = (formData.name || '').trim();

        const categoryValidationError = validateCategorySelectionForCreate(selectedCategoryId || '');
        if (categoryValidationError) {
            setCategoryIdError(categoryValidationError);
            setNewUnitSaveError(getUnitNameValidationMessage(categoryValidationError) || 'Equipment Category is required.');
            return;
        }
        const nameValidationError = validateUnitNameForCreate((formData.name as string) || '');
        if (nameValidationError) {
            setUnitNameError(nameValidationError);
            setNewUnitSaveError(getUnitNameValidationMessage(nameValidationError));
            return;
        }
        if (!unitName || unitName === 'New Unit') {
            setNewUnitSaveError('Unit Name / ID is required.');
            return;
        }

        const serialValidationError = validateSerialNumberForCreate(
            (formData.serialNumber as string) || '',
        );
        if (serialValidationError) {
            setSerialNumberError(serialValidationError);
            setNewUnitSaveError(getUnitNameValidationMessage(serialValidationError));
            return;
        }

        const licenseValidationError = validateLicenseNumberForCreate(
            (formData.licenseNumber as string) || '',
        );
        if (licenseValidationError) {
            setLicenseNumberError(licenseValidationError);
            setNewUnitSaveError(getUnitNameValidationMessage(licenseValidationError));
            return;
        }

        const engineSerialValidationError = validateEngineSerialNumberForCreate(
            (formData.engineSerialNumber as string) || '',
        );
        if (engineSerialValidationError) {
            setEngineSerialNumberError(engineSerialValidationError);
            setNewUnitSaveError(getUnitNameValidationMessage(engineSerialValidationError));
            return;
        }

        const existingCategory = companyGroupedEquipmentData.find((c) => c.id === selectedCategoryId);
        const resolvedInspectionFormId =
            newUnitInspectionFormId ||
            (selectedCategoryId !== PENDING_NEW_CATEGORY_ID ? existingCategory?.formId : undefined) ||
            null;

        setNewUnitSaveError(null);
        setIsSavingNewUnit(true);

        try {
            const today = new Date().toISOString().split('T')[0];
            let resolvedCategoryId = selectedCategoryId!;

            if (selectedCategoryId === PENDING_NEW_CATEGORY_ID) {
                const newCategory = await saveNewEquipmentCategory({
                    companyId,
                    name: pendingCategoryName!.trim(),
                    ...(resolvedInspectionFormId ? { formId: resolvedInspectionFormId } : {}),
                });
                addCategory(newCategory);
                resolvedCategoryId = newCategory.id;
            } else if (
                existingCategory &&
                resolvedInspectionFormId &&
                existingCategory.formId !== resolvedInspectionFormId
            ) {
                const updateInput: UpdateEquipmentCategoryInput = {
                    id: selectedCategoryId!,
                    formId: resolvedInspectionFormId,
                };
                const response = (await authClient.graphql({
                    query: updateEquipmentCategoryMutation,
                    variables: { input: updateInput },
                })) as any;
                updateCategory(selectedCategoryId!, response.data.updateEquipmentCategory);
            }

            let imagePath: string | undefined;
            if (newUnitImage) {
                const resizedImage = await resizeFile(newUnitImage);
                const imageFile = resizedImage as File;
                const fileName = `${uuidv4()}-IMAGE-${unitName}-${imageFile.name}`;
                const savedImage = await uploadData({
                    path: S3_IMAGE_PATH.fullEquipmentImagePath(companyId, fileName),
                    data: imageFile,
                }).result;
                imagePath = savedImage.path;
            }

            const unitInputRaw: Record<string, any> = {
                ...formData,
                name: unitName,
                categoryId: resolvedCategoryId,
                inputDate: formData.inputDate || today,
                inputByPerson:
                    formData.inputByPerson || companyOwner?.name || companyUser?.name,
                equipmentStatus: true,
                ...(imagePath ? { image: imagePath } : {}),
            };
            if (unitInputRaw.unitOfMeasure) {
                unitInputRaw.unitOfMeasure = normalizeUnitOfMeasure(unitInputRaw.unitOfMeasure);
                if (companyId && unitInputRaw.unitOfMeasure) {
                    await ensureCompanyUnitsOfMeasureSeeded(companyId, [unitInputRaw.unitOfMeasure]);
                }
            }
            if (unitInputRaw.manufactureDate) {
                unitInputRaw.manufactureDate = normalizeStoredYearValue(unitInputRaw.manufactureDate);
            }
            const unitInput = sanitizeGen2SingleUnitWritePayload(unitInputRaw, { dropEmptyStrings: true });

            const unitResponse = (await authClient.graphql({
                query: createSingleUnitMutation,
                variables: { input: unitInput },
            })) as any;
            const newUnit = unitResponse.data?.createSingleUnit;
            if (newUnit) {
                addUnit(newUnit);
                await refreshEquipment();
                clearEquipmentNewUnitDraft(draftStorageKeyRef.current);
                registerGuard(null);
                navigate('/equipment');
            } else {
                const graphQlMessage = unitResponse?.errors?.[0]?.message;
                setNewUnitSaveError(graphQlMessage || 'Failed to save unit. Please try again.');
            }
        } catch (error) {
            console.error('Error creating unit:', error);
            const message =
                (error as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
                (error instanceof Error ? error.message : null);
            setNewUnitSaveError(message || 'Failed to save unit. Please try again.');
        } finally {
            setIsSavingNewUnit(false);
        }
    };

    const selectedCategory = companyGroupedEquipmentData.find(
        (c) => c.id === formData.categoryId,
    );
    const categoryBadgeName =
        selectedCategory?.name ||
        (categoryId === PENDING_NEW_CATEGORY_ID ? pendingCategoryName : null);

    const isPendingNewCategory =
        Boolean(categoryId === PENDING_NEW_CATEGORY_ID && pendingCategoryName?.trim());

    return (
        <div className="page-container single-unit-page">
            <PageHeader
                as="h1"
                title="New Equipment"
                subtitle={
                    isPendingNewCategory
                        ? `New category “${pendingCategoryName}” will be created when you save this unit.`
                        : 'Fill in the Equipment details below'
                }
                breadcrumbs={[
                    { label: 'Equipment', href: '/equipment' },
                    { label: 'New Equipment', href: undefined },
                ]}
            />
            <UnsavedChangesBar dirty={hasNewUnitUnsavedChanges} variant="standalone">
                <span className="unsaved-changes-text" style={{ color: 'white' }}>
                    {isPendingNewCategory
                        ? `New category: ${pendingCategoryName} (saved with unit)`
                        : hasNewUnitUnsavedChanges
                          ? 'Unsaved changes'
                          : 'New unit — fill in details below'}
                </span>
                {newUnitSaveError && <span className="unsaved-changes-error">{newUnitSaveError}</span>}
                <div className="unsaved-changes-actions">
                    <Btn variant="secondary" buttonText="Cancel" buttonOnClick={() => tryNavigate('/equipment')} />
                    <Btn
                        variant="ghost"
                        buttonText={isSavingNewUnit ? 'Saving...' : 'Save'}
                        buttonDisabled={isSavingNewUnit}
                        buttonOnClick={handleSaveNewUnit}
                    />
                </div>
            </UnsavedChangesBar>

            {isPendingNewCategory && (
                <div className="equipment-new-category-callout" role="status" aria-live="polite">
                    <span className="equipment-category-pending-badge">New category</span>
                    <span className="equipment-new-category-callout-text">
                        <strong>{pendingCategoryName}</strong> is not in your equipment list yet. It will be added when
                        you save this unit.
                    </span>
                </div>
            )}

            <Card className="card-lg new-unit">
                <div className="unit-title-section">
                    <div className="unit-title-content">
                        <div className="unit-image-wrapper">
                            {newUnitImagePreviewUrl ? (
                                <img src={newUnitImagePreviewUrl} alt="Unit" className="unit-image" />
                            ) : (
                                <div className="unit-image unit-image-placeholder" aria-hidden="true">
                                    <IconEquipment iconClasses="unit-image-placeholder-icon" />
                                </div>
                            )}
                        </div>
                        <div className="unit-title-info">
                            {/* <div className="breadcrumb">Equipment &gt; New Unit</div> */}
                            <h2>
                                {(formData.name as string) || (
                                    <span style={{ color: '#94a3b8' }}>New Unit</span>
                                )}
                            </h2>
                            {categoryBadgeName && (
                                <span
                                    className={
                                        categoryId === PENDING_NEW_CATEGORY_ID && pendingCategoryName
                                            ? 'equipment-type-badge equipment-type-badge--new'
                                            : 'equipment-type-badge'
                                    }
                                >
                                    {categoryId === PENDING_NEW_CATEGORY_ID && pendingCategoryName && (
                                        <span className="equipment-type-badge-label">New</span>
                                    )}
                                    {categoryBadgeName}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="tab-content">
                    <EquipmentOverviewTab
                        unit={draftNewUnit}
                        isEditing={true}
                        isNewUnit={true}
                        priorityFields={NEW_UNIT_PRIORITY_FIELDS}
                        formData={formData}
                        setFormData={setFormData}
                        sectionConfigs={sectionConfigs}
                        fieldConfigs={newUnitFieldConfigs}
                        autosuggestOptions={autosuggestOptions}
                        unitOfMeasureExtraValues={unitOfMeasureExtraValues}
                        inspectionFormId={newUnitInspectionFormId}
                        onInspectionFormIdChange={setNewUnitInspectionFormId}
                        inspectionFormSelectVariant={canManageForms ? 'search-suggest' : 'select'}
                        inspectionFormActionOption={
                            canManageForms
                                ? {
                                      label: ADD_INSPECTION_FORM_OPTION_LABEL,
                                      onClick: handlePendingInspectionFormAdd,
                                  }
                                : undefined
                        }
                        dailyServiceFormSelectVariant={canManageForms ? 'search-suggest' : 'select'}
                        dailyServiceFormActionOption={
                            canManageForms
                                ? {
                                      label: ADD_DAILY_SERVICE_FORM_OPTION_LABEL,
                                      onClick: handlePendingDailyServiceFormAdd,
                                  }
                                : undefined
                        }
                        onCategoryIdChange={handleCategoryIdChange}
                        categorySelectVariant="search-suggest"
                        categoryActionOption={{
                            label: ADD_EQUIPMENT_TYPE_OPTION_LABEL,
                            onClick: handlePendingCategoryAdd,
                        }}
                        pendingCategoryName={pendingCategoryName}
                        unitImageUrl={newUnitImagePreviewUrl}
                        onImageSelect={setNewUnitImage}
                        fieldValidations={fieldValidations}
                        categoryInputId="new-unit-categoryId"
                        unitNameFocusGuard={unitNameFocusGuard}
                        companyId={companyId}
                        reloadServiceCenters={reloadServiceCenters}
                    />
                </div>
            </Card>
            <EquipmentUnsavedExitModal
                visible={showNewUnitExitModal}
                onDismiss={() => {
                    setShowNewUnitExitModal(false);
                    cancelPendingNavigation();
                }}
                onConfirmLeave={() => {
                    clearEquipmentNewUnitDraft(draftStorageKeyRef.current);
                    setShowNewUnitExitModal(false);
                    confirmPendingNavigation();
                }}
            />
            <EquipmentCategoryFormOverlay
                visible={showCategoryOverlay}
                defaultCategoryName={pendingCategoryName || ''}
                onDismiss={() => {
                    setShowCategoryOverlay(false);
                    setPendingCategoryName(null);
                }}
                onCategoryCreated={(category) => {
                    setCategoryIdError(null);
                    setPendingCategoryName(null);
                    setFormData((prev: Partial<SingleUnit>) => ({ ...prev, categoryId: category.id }));
                }}
            />
            <FormBuilderOverlay
                visible={showInspectionFormOverlay}
                defaultFormName={pendingInspectionFormName}
                formType={FORM_TYPES.INSPECTION}
                onDismiss={() => {
                    setShowInspectionFormOverlay(false);
                    setPendingInspectionFormName('');
                }}
                onFormSaved={(form) => {
                    setNewUnitInspectionFormId(form.id);
                    setPendingInspectionFormName('');
                }}
            />
            <FormBuilderOverlay
                visible={showDailyServiceFormOverlay}
                defaultFormName={pendingDailyServiceFormName}
                formType={FORM_TYPES.DAILY_SERVICE}
                onDismiss={() => {
                    setShowDailyServiceFormOverlay(false);
                    setPendingDailyServiceFormName('');
                }}
                onFormSaved={(form) => {
                    setFormData((prev: Partial<SingleUnit>) => ({ ...prev, dailyServiceLogFormId: form.id }));
                    setPendingDailyServiceFormName('');
                }}
            />
        </div>
    );
};
